import { useState, useEffect } from 'react'
import api from '../../services/apiService'

/**
 * The signed-in unit's match schedule: which match, on what date, at what time,
 * on which court. Grouped by day, because that is how a match day is planned.
 */
const MyFixtures = () => {
  const [fixtures, setFixtures] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [sport, setSport] = useState('all')

  useEffect(() => { load() }, [])

  const load = async () => {
    setLoading(true)
    setError('')
    try {
      const res = await api.get('/user/fixtures')
      setFixtures(res.data?.data || [])
    } catch (err) {
      console.error('Error loading fixtures:', err)
      setError('Could not load your match schedule.')
    } finally {
      setLoading(false)
    }
  }

  // Sport name sits ahead of the dash in the event name: "Badminton — Gurgaon".
  const sportOf = (f) => (f.event_name || '').split('—')[0].trim() || 'Other'
  const sports = [...new Set(fixtures.map(sportOf))].sort()

  const shown = sport === 'all' ? fixtures : fixtures.filter((f) => sportOf(f) === sport)

  // Preserve the order the API returned, which is already date then time.
  const byDay = shown.reduce((acc, f) => {
    const key = f.day || 'Not yet scheduled'
    ;(acc[key] = acc[key] || []).push(f)
    return acc
  }, {})

  if (loading) return <div style={wrap}><p>Loading your match schedule…</p></div>

  return (
    <div style={wrap}>
      <div style={header}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26 }}>Match Schedule</h1>
          <p style={{ margin: '4px 0 0', color: '#5c6b7f' }}>
            Every match your members are drawn into, with date, time and court.
          </p>
        </div>
        <button onClick={load} style={refreshBtn}>Refresh</button>
      </div>

      {error && <div style={errorBox}>{error}</div>}

      {fixtures.length === 0 && !error ? (
        <div style={emptyBox}>
          <strong>No matches drawn yet.</strong>
          <p style={{ margin: '6px 0 0', color: '#5c6b7f' }}>
            Once the league office generates the draw for an event your members
            are entered in, their matches appear here.
          </p>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '18px 0' }}>
            <Chip active={sport === 'all'} onClick={() => setSport('all')}>
              All sports ({fixtures.length})
            </Chip>
            {sports.map((s) => (
              <Chip key={s} active={sport === s} onClick={() => setSport(s)}>
                {s} ({fixtures.filter((f) => sportOf(f) === s).length})
              </Chip>
            ))}
          </div>

          {Object.entries(byDay).map(([day, list]) => (
            <div key={day} style={{ marginBottom: 26 }}>
              <h2 style={dayHeading}>
                {day} <span style={{ color: '#8b95a5', fontWeight: 500, fontSize: 14 }}>
                  · {list.length} match{list.length === 1 ? '' : 'es'}
                </span>
              </h2>

              <div style={{ display: 'grid', gap: 8 }}>
                {list.map((f) => (
                  <div key={f.id} style={card}>
                    <div style={timeCol}>
                      <div style={{ fontSize: 17, fontWeight: 700 }}>{f.start_time || '—'}</div>
                      <div style={{ fontSize: 12, color: '#8b95a5' }}>{f.slot_minutes} min</div>
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#22303f' }}>
                        {f.home} <span style={{ color: '#a0a9b5', fontWeight: 500 }}>vs</span> {f.away}
                      </div>
                      <div style={{ fontSize: 12.5, color: '#5c6b7f', marginTop: 3 }}>
                        {f.category} · {f.round_name}
                        {f.group_name ? ` · Group ${f.group_name}` : ''}
                      </div>
                    </div>

                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={courtPill}>{f.court || 'Court TBC'}</div>
                      <div style={{ fontSize: 11.5, color: '#8b95a5', marginTop: 4 }}>{f.venue}</div>
                      {f.status === 'completed' && (
                        <div style={{ fontSize: 13, fontWeight: 700, marginTop: 4 }}>
                          {f.home_score} – {f.away_score}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  )
}

const Chip = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    aria-pressed={active}
    style={{
      padding: '6px 14px', borderRadius: 20, cursor: 'pointer', fontSize: 13,
      border: `1px solid ${active ? '#a4262c' : '#d9dee5'}`,
      background: active ? '#a4262c' : '#fff',
      color: active ? '#fff' : '#42505f',
      fontWeight: active ? 700 : 500,
    }}
  >
    {children}
  </button>
)

const wrap = { padding: 24, maxWidth: 1100, margin: '0 auto' }
const header = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }
const refreshBtn = { padding: '8px 18px', border: '1px solid #d9dee5', background: '#fff', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }
const errorBox = { padding: 14, background: '#fdeceb', border: '1px solid #f3c4c0', borderRadius: 8, color: '#a4262c', marginTop: 16 }
const emptyBox = { padding: 26, background: '#f7f9fb', border: '1px dashed #d3dae2', borderRadius: 10, marginTop: 20 }
const dayHeading = { fontSize: 17, margin: '0 0 10px', paddingBottom: 7, borderBottom: '2px solid #e4e9ef' }
const card = { display: 'flex', gap: 16, alignItems: 'center', padding: '12px 16px', background: '#fff', border: '1px solid #e4e9ef', borderRadius: 10 }
const timeCol = { minWidth: 62, textAlign: 'center', borderRight: '1px solid #eef1f5', paddingRight: 14 }
const courtPill = { display: 'inline-block', padding: '3px 11px', borderRadius: 6, background: '#eef4fb', color: '#28527a', fontSize: 12.5, fontWeight: 700 }

export default MyFixtures
